import type { AnalyticsEvent } from './analytics';
import { captureMirrorFailure } from './monitoring';
import { eventMirrorEndpoint } from './eventMirrorEndpoint';

/**
 * The browser half of `POST /api/v1/events` — every product-analytics event
 * this app captures, mirrored into the same `user_events` table the ~31 server
 * and worker call sites write to, so the admin console reads one activity log
 * instead of correlating two.
 *
 * WHY THIS IS NOT `services/api.ts`. Its response interceptor turns any 401
 * into a `localStorage` wipe + `window.location.href = '/signin?redirect=...'`,
 * so a background analytics flush that raced a token expiry would navigate the
 * user off the page they are working on. It also hands every failure to
 * `captureApiError`, so one offline user would emit a Sentry event every five
 * seconds. `fetch` reaches the same server with neither attached.
 *
 * (This note used to claim a third reason — that api.ts's 401 handler calls
 * `captureEvent('session_expired')`, which enqueues here, which flushes, which
 * 401s again. There is no such loop: api.ts removes `accessToken` BEFORE that
 * capture, so `enqueueEvent`'s own guard returns without queueing and there is
 * nothing to flush. The redirect stands on its own.)
 *
 * Bypassing api.ts does NOT bypass Sentry's fetch instrumentation, which is an
 * SDK integration and sees every request the page makes. A flush every five
 * seconds fills the 100-entry breadcrumb ring in about eight minutes, evicting
 * the user actions that explain an error. The filter for that lives in
 * `beforeBreadcrumb` in services/monitoring.ts, keyed on this endpoint.
 *
 * WHAT THIS MODULE DOES NOT DO: it never retries. A flush that fails — offline,
 * 500, 401, or a batch the server `dropped` for running out of its 5s budget —
 * loses those events, permanently and silently. That is deliberate. There is no
 * rate limiter in front of this endpoint (the backend router says so), it is
 * already the highest-frequency route in the app, and a retry loop in every
 * open browser is the shape that turns a slow database into an outage. Client
 * analytics is lossy by construction; the activity log's server-side writes are
 * the record that has to be complete.
 *
 * It does read `response.status`, and nothing else. Not to retry, but because
 * without it a revoked token (401), a role regression (403), contract drift
 * (422) and a deploy (502) are the same silence — and one of those is a bug
 * that will not fix itself. The status arrives with the headers, so reading it
 * consumes no body and blocks on nothing, which is what the unload path needs.
 * See `noteFlushOutcome`. The 202 body stays unread: its `accepted` count is
 * the server's word for ATTEMPTED — `record_event` swallows failures below
 * `BaseException` — so consuming it would not make delivery any truer.
 */

/** One queued event, in the wire shape `ClientEventInDTO` parses. */
export interface PendingEvent {
  name: AnalyticsEvent;
  occurredAt: string;
  entityType?: string;
  entityId?: string;
  properties?: Record<string, unknown>;
}

/** Server cap: `ClientEventsInDTO.events` is `max_length=50`. 51 is a 422. */
const MAX_BATCH = 50;

/**
 * Under the 64 KiB (65536-byte) body limit the Fetch standard puts on
 * `keepalive` requests, with room for headers and framing. A batch over the
 * limit is not truncated by the browser — the whole request is rejected before
 * it leaves — so the unload flush is exactly where an uncapped payload would
 * lose everything. `MAX_BATCH` alone does not bound this: the server accepts
 * 8192 bytes of `properties` PER EVENT, so fifty legal events are ~400 KB.
 */
const MAX_BODY_BYTES = 60000;

/** `{"events":[]}` — the fixed cost of the envelope around the batch. */
const ENVELOPE_BYTES = 13;

/**
 * Server cap: `ClientEventInDTO.properties` is validated at
 * MAX_CLIENT_PROPERTIES_BYTES = 8192 bytes of UTF-8. Exceeding it is a
 * VALIDATION error on the envelope, which 422s the entire batch — so one
 * oversized event would take the other forty-nine down with it. ONCE, not
 * repeatedly: nothing retries and packBatch() has already removed the batch
 * from the queue, so the cost is those forty-nine events and no more.
 *
 * Bounded at enqueue instead — against the byte count the SERVER computes,
 * which is not the one `JSON.stringify` produces. See `serverSeparatorBytes`.
 */
const MAX_PROPERTIES_BYTES = 8192;

const FLUSH_MS = 5000;

const queue: PendingEvent[] = [];
let timer: number | null = null;

const endpoint = eventMirrorEndpoint;

/**
 * Bytes a string costs as UTF-8, without `TextEncoder` — jsdom 27 (what CRA 5's
 * jest environment runs) does not define it, so reaching for it would make this
 * module throw in every test that touches a flush.
 */
export const utf8ByteLength = (value: string): number => {
  let bytes = 0;
  for (let i = 0; i < value.length; i += 1) {
    const code = value.charCodeAt(i);
    if (code < 0x80) bytes += 1;
    else if (code < 0x800) bytes += 2;
    else if (code >= 0xd800 && code <= 0xdbff && i + 1 < value.length) {
      // A well-formed surrogate pair is one 4-byte code point; a lone high
      // surrogate falls through to 3, which is what JSON.stringify's `\udXXX`
      // escape costs to within a few bytes on a payload budget of 60 KB.
      const next = value.charCodeAt(i + 1);
      if (next >= 0xdc00 && next <= 0xdfff) {
        bytes += 4;
        i += 1;
      } else bytes += 3;
    } else bytes += 3;
  }
  return bytes;
};

/**
 * The bytes Python's `json.dumps` adds that `JSON.stringify` does not.
 *
 * dtos.py measures the cap as
 * `json.dumps(value, default=str, ensure_ascii=False).encode("utf-8")` — with
 * Python's DEFAULT separators, `", "` and `": "`, each one byte wider than the
 * compact pair `JSON.stringify` emits. A flat N-key object is therefore 2N-1
 * bytes larger on the server than here, so `properties` the client measures at
 * exactly 8192 is 8193 there: a ValueError, which Pydantic reports as a 422 on
 * the ENVELOPE and which discards up to forty-nine unrelated events with it.
 *
 * Counted rather than covered by a flat safety margin because the gap is a
 * function of SHAPE, not of size: one 8 KB string costs 1 extra byte, a
 * 200-key object costs 399, and nesting adds its own at every level — so any
 * fixed reduction is either wrong for the first case or wrong for the second.
 *
 * Iterative rather than recursive so that a pathologically nested payload
 * cannot overflow the stack inside a capture.
 *
 * Exact for STRUCTURE, at every nesting depth, including empty containers.
 * The one residual is float formatting: Python's repr pads a single-digit
 * exponent, so a magnitude in [1e-9, 1e-6) costs one byte this does not count
 * (`1.5e-7` vs `1.5e-07`). Everything else -- integers, ordinary decimals,
 * >=1e21, <=1e-10 -- matches exactly. One byte against the 1-399 this replaces,
 * and sub-microsecond floats are not a thing in analytics properties; recorded
 * so the next reader does not take "reproduces the server's byte count" as
 * unconditional.
 */
const serverSeparatorBytes = (parsed: unknown): number => {
  let extra = 0;
  const stack: unknown[] = [parsed];
  while (stack.length > 0) {
    const value = stack.pop();
    if (Array.isArray(value)) {
      // `", "` between elements.
      extra += Math.max(value.length - 1, 0);
      for (const item of value) stack.push(item);
    } else if (value !== null && typeof value === 'object') {
      const members = Object.values(value as Record<string, unknown>);
      // `": "` after every key, plus `", "` between the members.
      extra += members.length + Math.max(members.length - 1, 0);
      for (const item of members) stack.push(item);
    }
  }
  return extra;
};

/**
 * `properties` the server will accept, or a marker saying why it could not.
 *
 * The event itself always survives: knowing that `resume_fix_accepted` happened
 * with unreadable properties is worth more than not knowing it happened.
 *
 * Returns a DETACHED deep copy, not the caller's object. The bound is checked
 * once, here, but the value is not serialised until the flush up to five
 * seconds later — so a caller that keeps its reference and mutates it in
 * between (a props object reused across renders, an array pushed to as more
 * arrives) would defeat the check and produce exactly the 422 this function
 * exists to prevent. The copy is the JSON round-trip the wire performs anyway.
 */
const boundedProperties = (
  properties?: Record<string, unknown>
): Record<string, unknown> | undefined => {
  if (properties === undefined || properties === null) return undefined;

  let encoded: string;
  let snapshot: unknown;
  try {
    encoded = JSON.stringify(properties);
    // Parsing back is what detaches the copy, and it is also exactly the
    // structure the server re-dumps: `toJSON` hooks have run, `undefined`
    // members are gone, and a `Map` is already the `{}` it serialises to.
    snapshot = JSON.parse(encoded);
  } catch {
    // A circular reference, a BigInt, or a `toJSON` that returned undefined —
    // JSON.stringify throws on the first two and returns undefined for the
    // third, which JSON.parse then rejects. flush() stringifies the WHOLE
    // batch, so any of them left in the queue takes every event beside it out.
    return { _propertiesDropped: 'unserializable' };
  }

  // A LONE SURROGATE is valid JSON and valid UTF-8 on the wire, so it reaches
  // the server intact -- where `json.dumps(..., ensure_ascii=False).encode()`
  // raises UnicodeEncodeError. That is a ValueError subclass, so the DTO's
  // `except (TypeError, ValueError)` turns it into a validation error and 422s
  // the WHOLE envelope, discarding up to 49 innocent events.
  //
  // Not reachable from any call site today -- a well-formed pair is emitted raw
  // and is safe. It becomes reachable the first time someone puts
  // `title.slice(0, 100)` into a prop, which is the likeliest future addition.
  // ES2019's well-formed JSON.stringify escapes exactly and only lone
  // surrogates, so the escape sequence is a precise detector.
  //
  // BOTH HALVES OF THE RANGE, `\ud800`-`\udfff`: high surrogates are
  // `\ud800`-`\udbff` (second hex digit 8/9/a/b) and low ones are
  // `\udc00`-`\udfff` (c/d/e/f). An earlier `[89ab]` here matched only the
  // high half -- so a lone LOW surrogate passed, which is exactly what
  // `title.slice(0, 100)` produces when it cuts an emoji at its SECOND half,
  // the very case the paragraph above names as the likeliest future addition.
  if (/\\ud[89a-f][0-9a-f]{2}/i.test(encoded)) {
    return { _propertiesDropped: 'unserializable' };
  }

  // `ClientEventInDTO.properties` is typed `dict`. An array serialises to
  // `[...]` and a `toJSON` can return a scalar; either is a 422 on the whole
  // envelope, so neither may reach the wire.
  if (snapshot === null || typeof snapshot !== 'object' || Array.isArray(snapshot)) {
    return { _propertiesDropped: 'not-an-object' };
  }

  if (utf8ByteLength(encoded) + serverSeparatorBytes(snapshot) > MAX_PROPERTIES_BYTES) {
    return { _propertiesDropped: 'oversized' };
  }
  return snapshot as Record<string, unknown>;
};

/** `entity_id` is typed `UUID` server-side; anything else 422s the whole batch. */
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** `entity_type` is `max_length=40`; over that is a 422 for the whole batch too. */
const MAX_ENTITY_TYPE_CHARS = 40;

/**
 * The entity columns, but only when both are shapes the DTO validates.
 *
 * NO CALLER SUPPLIES THESE TODAY. `captureEvent` runs every event through here,
 * but not one of the ~100 capture sites passes `entityType`/`entityId` in its
 * props, so every mirrored row currently lands with both columns NULL. This is
 * a contract for the sites that will: the server types `entity_id` as `UUID`
 * and `entity_type` at `max_length=40`, so a site that reaches for these with
 * an `entityId: '42'` in hand needs them dropped rather than sent — a bad one
 * poisons every batch that event rides in, and the failure is nearly invisible
 * from here, since a flush reports only a status class and only in bulk.
 */
export const entityFields = (
  properties?: Record<string, unknown>
): { entityType?: string; entityId?: string } => {
  if (!properties) return {};
  const type = properties.entityType;
  const id = properties.entityId;
  if (typeof type !== 'string' || type.length === 0 || type.length > MAX_ENTITY_TYPE_CHARS) return {};
  if (typeof id !== 'string' || !UUID_PATTERN.test(id)) return {};
  return { entityType: type, entityId: id };
};

const schedule = (): void => {
  if (timer !== null) return;
  timer = window.setTimeout(() => {
    // Cleared BEFORE the flush, not after: leave it set and the guard above
    // never opens again, so every event after the first flush waits for a
    // 50-event batch or a page hide instead of the 5s window.
    timer = null;
    void flush();
  }, FLUSH_MS);
};

/**
 * Queue one event for the activity log.
 *
 * Returns without queueing when there is no `accessToken`. The endpoint is
 * authenticated and the server attributes an event to whoever the FLUSH-time
 * token names, so an event captured on the sign-in page would be recorded
 * against the next account to sign in on this browser — not merely lost, but
 * wrong. It also bounds the queue for a visitor who never authenticates.
 */
export function enqueueEvent(event: {
  name: AnalyticsEvent;
  entityType?: string;
  entityId?: string;
  properties?: Record<string, unknown>;
}): void {
  if (!localStorage.getItem('accessToken')) return;

  queue.push({
    ...event,
    properties: boundedProperties(event.properties),
    // The server clamps this to `min(occurredAt, now)` and rejects anything
    // older than 24 hours, so it has to be a real instant rather than a
    // flush-time stamp.
    occurredAt: new Date().toISOString(),
  });

  if (queue.length >= MAX_BATCH) {
    void flush();
    return;
  }
  schedule();
}

/**
 * As many events off the front of `source` as one request may legally carry,
 * REMOVED from `source`. Whatever is left stays there for the next flush.
 *
 * Takes its bounds as arguments so the head-of-line case below is reachable
 * from a test: with the shipped values it is not, because `boundedProperties`
 * caps an event at ~8 KB and the body budget is 60 KB. It stays because the
 * two constants are independent — raise `MAX_PROPERTIES_BYTES` past
 * `MAX_BODY_BYTES` (the server's own per-event cap could rise) and without it
 * the queue stops forever at the first event that does not fit.
 */
export function packBatch(
  source: PendingEvent[],
  maxBytes: number = MAX_BODY_BYTES,
  maxCount: number = MAX_BATCH
): PendingEvent[] {
  const batch: PendingEvent[] = [];
  let bytes = ENVELOPE_BYTES;

  while (source.length > 0 && batch.length < maxCount) {
    const next = source[0];
    const size = utf8ByteLength(JSON.stringify(next)) + (batch.length > 0 ? 1 : 0);
    if (bytes + size > maxBytes) {
      // Nothing packed yet means this one event cannot fit in ANY request.
      if (batch.length === 0) source.shift();
      break;
    }
    source.shift();
    batch.push(next);
    bytes += size;
  }
  return batch;
}

/**
 * Consecutive flushes that failed with something transient-looking. A 5xx is a
 * deploy or a wedged database and a rejected `fetch` is usually a tunnel, a
 * captive portal or a laptop lid — every one of them normal on its own, and all
 * of them recover without anybody being told. What is not normal is that they
 * do not stop, and this counter is the only way to tell those apart from here.
 */
let consecutiveFailures = 0;

/** Already told Sentry about a rejected contract on this page. */
let contractFailureReported = false;

/**
 * How many consecutive failures before one is worth a Sentry event: 5 flushes,
 * so ~25 seconds of a client that cannot deliver anything at all.
 */
const FAILURES_BEFORE_REPORT = 5;

/**
 * What the server said, turned into a bounded signal. Still no retry: this
 * decides whether anybody is TOLD, never whether anything is sent again.
 *
 * Keyed on status class because the four failures behind the old silence need
 * opposite handling. A 422 is a contract bug — a property shape the DTO
 * refuses, an `entity_id` that is not a UUID, an event name off the server's
 * whitelist — and it is never transient: the same batch will be refused by
 * every replica, on every deploy, until someone changes code. It is reported
 * on sight, once per page load, WITH the batch's event names, because the name
 * is the only thing in the payload that points at the call site to fix.
 *
 * Everything else waits for a streak. A single 401 is what an expired session
 * looks like, a single 502 is what a deploy looks like, and a single rejected
 * `fetch` is what a train tunnel looks like — reporting any of those would bury
 * the signal in the noise of normal browsing. `navigator.onLine` is
 * deliberately NOT consulted: it reports link state, not reachability, so it is
 * false on a captive portal and true on a dead uplink, and gating on it would
 * both suppress real failures and admit imaginary ones.
 *
 * Any 2xx clears the counter, so only an UNBROKEN run reports.
 */
const noteFlushOutcome = (status: number | null, batch: PendingEvent[]): void => {
  if (status !== null && status >= 200 && status < 300) {
    consecutiveFailures = 0;
    return;
  }

  if (status === 422) {
    consecutiveFailures = 0;
    if (contractFailureReported) return;
    contractFailureReported = true;
    captureMirrorFailure({
      status,
      consecutiveFailures: 1,
      eventNames: Array.from(new Set(batch.map((event) => event.name))),
    });
    return;
  }

  consecutiveFailures += 1;
  // Exactly at the threshold, not past it: one report per streak, not one per
  // flush for as long as the network stays down.
  if (consecutiveFailures !== FAILURES_BEFORE_REPORT) return;
  captureMirrorFailure({
    status: status ?? 'network',
    consecutiveFailures,
    eventNames: Array.from(new Set(batch.map((event) => event.name))),
  });
};

/**
 * Send one batch. `useKeepalive` is for the page-hide path, where an ordinary
 * fetch is cancelled with the document.
 *
 * `keepalive: true` rather than `navigator.sendBeacon`: beacon cannot set
 * headers, so the only way to authenticate it would be a token in the query
 * string — which this API has no fallback for (`OAuth2PasswordBearer` reads the
 * `Authorization` header and nothing else, so every beacon would 401), and
 * which would write a bearer credential into server access logs, proxy logs and
 * browser history on the way to failing.
 */
export async function flush(useKeepalive = false): Promise<void> {
  if (queue.length === 0) return;

  const token = localStorage.getItem('accessToken');
  if (!token) {
    // Sign-out or an expired session cleared it. These events cannot be
    // attributed to anyone: sending without a token is a 401, and holding them
    // until someone else signs in on this browser records one user's activity
    // against another's account. Dropped, deliberately.
    queue.length = 0;
    return;
  }
  if (typeof fetch !== 'function') {
    queue.length = 0;
    return;
  }

  const batch = packBatch(queue);
  // The queue can still hold a remainder the byte budget would not take. Re-arm
  // so it leaves on the next window instead of waiting for the next captured
  // event, which on a page the user has stopped interacting with never comes.
  if (queue.length > 0) schedule();
  if (batch.length === 0) return;

  try {
    const response = await fetch(endpoint(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ events: batch }),
      keepalive: useKeepalive,
    });
    noteFlushOutcome(typeof response?.status === 'number' ? response.status : null, batch);
  } catch {
    // Offline, DNS, CORS, aborted by the unload. No retry — see the module
    // note. Swallowed rather than rejected because every caller is `void`.
    noteFlushOutcome(null, batch);
  }
}

let onVisibilityChange: (() => void) | null = null;
let onPageHide: (() => void) | null = null;

/**
 * Flush what is queued when the page goes away. Call once, at boot.
 *
 * Both events are registered because neither covers every way a page is left:
 * `visibilitychange` does not fire on a same-document bfcache restore, and
 * `pagehide` does not fire when a tab is merely backgrounded. (`pagehide` is
 * the RELIABLE half on iOS Safari — the events that browser has historically
 * skipped are `unload` and `beforeunload`, which is why neither is used here.
 * An earlier version of this comment had that backwards.)
 *
 * On an ordinary navigation BOTH fire, and that is usually one send and not
 * two: packBatch() removes the batch from the queue synchronously, before the
 * first request is built, so the second handler finds nothing left. The
 * exception is a queue the byte budget could not take in one go. Then the
 * second handler sends the remainder, and the two bodies compete for the same
 * 64 KiB keepalive allowance — which the Fetch standard scopes to all in-flight
 * keepalive requests of an ORIGIN, not to one request — so at MAX_BODY_BYTES
 * apiece the second is rejected before it leaves. Silently, like every other
 * failure here; those events are gone.
 */
export function installFlushOnHide(): void {
  if (onPageHide) return;

  onVisibilityChange = () => {
    if (document.visibilityState === 'hidden') void flush(true);
  };
  onPageHide = () => {
    void flush(true);
  };
  document.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('pagehide', onPageHide);
}

/**
 * Drop the module-level queue, timer and hide listeners.
 *
 * FOR TESTS. All of it is module state, and CRA's `resetMocks: true` resets
 * mock functions only — so without this, one test's unflushed events arrive in
 * the next test's batch, its `pagehide` handler keeps firing into it, and the
 * once-per-page-load report below has already been spent.
 */
export function __resetEventMirrorForTests(): void {
  queue.length = 0;
  consecutiveFailures = 0;
  contractFailureReported = false;
  if (timer !== null) window.clearTimeout(timer);
  timer = null;
  if (onVisibilityChange) document.removeEventListener('visibilitychange', onVisibilityChange);
  if (onPageHide) window.removeEventListener('pagehide', onPageHide);
  onVisibilityChange = null;
  onPageHide = null;
}

/** FOR TESTS: how many events are waiting. */
export function __queuedCountForTests(): number {
  return queue.length;
}
